// ────────────────────────────────────────────────────────────────────────────
// Career Intelligence — curated reference lists for consistent input.
//
// Management + tester inputs should GUIDE toward canonical values (schools, majors,
// locations, role titles) instead of free-text-only boxes, so the same school /
// major / location resolves the same way for everyone. These power native <datalist>
// autocomplete (suggested, but a value not in the list is still allowed — the lists
// are curated, not exhaustive) and fixed chip/select sets where the vocabulary is
// closed (career tracks, semesters).
//
// Not a DB table: this is a client-shipped curated seed. The fully canonical
// institution (IPEDS) + CIP-major reference tables remain a later net-new data task;
// this delivers the consistency win now.
// ────────────────────────────────────────────────────────────────────────────

// Closed vocabulary — career tracks (matches the career_track CHECK).
export const CAREER_TRACKS: { key: string; label: string }[] = [
  { key: "ai_ml", label: "AI / ML" },
  { key: "software", label: "Software" },
  { key: "data", label: "Data" },
  { key: "quant", label: "Quant" },
  { key: "fde", label: "Forward Deployed" },
  { key: "product", label: "Product" },
  { key: "design", label: "Design" },
  { key: "hardware", label: "Hardware" },
  { key: "security", label: "Security" },
  { key: "investing", label: "Investing" },
  { key: "finance", label: "Finance" },
  { key: "consulting", label: "Consulting" },
  { key: "marketing", label: "Marketing" },
  { key: "operations", label: "Operations" },
  { key: "life_sciences", label: "Life Sciences" },
  // Broad non-tech families — a general internship platform, not a tech-only one.
  { key: "engineering", label: "Engineering" },
  { key: "healthcare", label: "Healthcare" },
  { key: "education", label: "Education" },
  { key: "sales", label: "Sales & BD" },
  { key: "accounting", label: "Accounting" },
  { key: "legal", label: "Legal" },
  { key: "research", label: "Research" },
  { key: "supply_chain", label: "Supply Chain" },
  { key: "communications", label: "Communications" },
  { key: "human_resources", label: "Human Resources" },
  { key: "policy", label: "Policy & Government" },
  { key: "sustainability", label: "Sustainability & Energy" },
  { key: "hospitality", label: "Hospitality & Tourism" },
]

const CAREER_TRACK_LABEL = new Map(CAREER_TRACKS.map((t) => [t.key, t.label]))
/** Human label for a career-track code (e.g. "ai_ml" -> "AI / ML"). Falls back to a readable form of
 *  an unknown code so a raw key like "ai_ml" is NEVER shown to a student. Pure, so engines can use it. */
export function trackLabel(code: string | null | undefined): string {
  if (!code) return ""
  return CAREER_TRACK_LABEL.get(code) ?? code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export const SEMESTERS = ["Fall", "Spring", "Summer", "Winter"]

// Common US degrees, most-frequent first (undergrad, then graduate, then associate). Powers a
// <datalist> suggestion list - a value not in the list is still allowed, which IS the "Other"
// path (a student types their own). Curated, not exhaustive.
export const DEGREES = [
  "BS", "BA", "BBA", "BFA", "BSE", "BEng", "BSN", "BArch", "BSW",
  "MS", "MA", "MBA", "MEng", "MFA", "MSW", "MPH", "MPA", "LLM",
  "PhD", "JD", "MD", "DO", "PharmD", "DDS", "EdD",
  "AS", "AA", "AAS",
]

// Dropdown lists are shown ALPHABETICALLY (asc) so a student can scan them. The raw
// arrays below stay authored/grouped for readability; the exported names are sorted.
// Locations pin "Anywhere"/"Remote" first (they're intent, not a place), then sort.
const ascAlpha = (a: readonly string[]): string[] => [...a].sort((x, y) => x.localeCompare(y))
const pinnedAscAlpha = (pins: readonly string[], a: readonly string[]): string[] =>
  [...pins, ...ascAlpha(a.filter((x) => !pins.includes(x)))]

// Broad interest areas — wider than the 6 matching tracks, for capturing what a student is
// into. Multi-select + custom allowed. Steers recommendations; not limited to the tracks.
const RAW_INTEREST_AREAS = [
  "Software Engineering", "Artificial Intelligence", "Machine Learning", "Data Science", "Data Analytics",
  "Quantitative Trading", "Investing", "Private Equity", "Venture Capital", "Investment Banking",
  "Consulting", "Product Management", "Product Design", "UX / UI Design", "Marketing", "Growth",
  "Sales", "Business Development", "Operations", "Strategy", "Entrepreneurship", "Startups",
  "Fintech", "Cybersecurity", "Cloud / Infrastructure", "DevOps", "Robotics", "Hardware",
  "Autonomous Vehicles", "Healthcare", "Biotech", "Climate / Energy", "Sustainability",
  "Gaming", "Media / Entertainment", "Social Impact", "Nonprofit", "Public Policy", "Government",
  "Education", "Real Estate", "Supply Chain", "Research", "Analytics", "Sales Engineering",
]

// Comprehensive US metro list (+ Anywhere / Remote) — a student can still type one not listed.
// "Anywhere" means open to any location (no geographic constraint).
const RAW_LOCATIONS = [
  "Anywhere", "Remote", "New York", "Brooklyn", "Jersey City", "Newark", "Stamford", "Boston", "Cambridge",
  "Providence", "Hartford", "Philadelphia", "Pittsburgh", "Washington DC", "Arlington", "Alexandria",
  "Bethesda", "Baltimore", "Richmond", "Virginia Beach", "Raleigh", "Durham", "Chapel Hill", "Charlotte",
  "Atlanta", "Savannah", "Charleston", "Nashville", "Memphis", "Birmingham", "New Orleans", "Miami",
  "Tampa", "Orlando", "Jacksonville", "Chicago", "Madison", "Milwaukee", "Minneapolis", "Detroit",
  "Ann Arbor", "Columbus", "Cleveland", "Cincinnati", "Indianapolis", "Kansas City", "St. Louis",
  "Omaha", "Des Moines", "Dallas", "Fort Worth", "Austin", "Houston", "San Antonio", "Oklahoma City",
  "Denver", "Boulder", "Colorado Springs", "Salt Lake City", "Provo", "Phoenix", "Mesa", "Tucson",
  "Las Vegas", "Albuquerque", "San Francisco", "Oakland", "Berkeley", "Palo Alto", "Mountain View",
  "Sunnyvale", "San Jose", "Santa Monica", "Pasadena", "Los Angeles", "Long Beach", "Irvine", "San Diego",
  "Sacramento", "Fresno", "Seattle", "Bellevue", "Redmond", "Portland",
]

// Broad set of industries — student-facing multi-select on the tester + preferences.
const RAW_INDUSTRIES = [
  "Technology", "Software", "Artificial Intelligence", "Data & Analytics", "Cybersecurity",
  "Fintech", "Financial Services", "Investment Banking", "Private Equity", "Venture Capital",
  "Asset Management", "Insurance", "Accounting", "Management Consulting", "Consulting",
  "Healthcare", "Biotechnology", "Pharmaceuticals", "Medical Devices", "Public Health",
  "Marketing & Advertising", "Media & Entertainment", "Gaming", "Journalism & Publishing",
  "Retail & E-commerce", "Consumer Goods (CPG)", "Fashion & Apparel", "Food & Beverage",
  "Hospitality & Travel", "Real Estate", "Construction", "Manufacturing", "Automotive",
  "Aerospace & Defense", "Energy & Utilities", "Climate & Sustainability",
  "Transportation & Logistics", "Telecommunications", "Agriculture", "Education",
  "Government & Public Sector", "Nonprofit & Social Impact", "Legal", "Human Resources", "Sports",
]

// Comprehensive intern role titles across every industry (not just the tech tracks).
const RAW_ROLE_TITLES = [
  // Software / data / AI
  "Software Engineer Intern", "Machine Learning Engineer Intern", "ML Research Intern", "AI Engineer Intern",
  "Data Scientist Intern", "Data Analyst Intern", "Data Engineer Intern", "Analytics Engineer Intern",
  "Backend Engineer Intern", "Frontend Engineer Intern", "Full Stack Engineer Intern", "DevOps Engineer Intern",
  "Security Engineer Intern", "Systems Engineer Intern", "Platform Engineer Intern", "QA Engineer Intern",
  "Research Scientist Intern", "Product Manager Intern", "Product Analyst Intern", "Technical Program Manager Intern",
  // Quant / finance / investing
  "Quantitative Researcher Intern", "Quantitative Trader Intern", "Quantitative Developer Intern",
  "Investment Banking Analyst Intern", "Private Equity Analyst Intern", "Venture Capital Analyst Intern",
  "Equity Research Intern", "Investment Analyst Intern", "Financial Analyst Intern", "Risk Analyst Intern",
  "Credit Analyst Intern", "Actuarial Intern", "Wealth Management Intern",
  // Accounting
  "Accounting Intern", "Audit Intern", "Tax Intern", "Finance Intern",
  // Consulting / strategy / ops
  "Consulting Analyst Intern", "Strategy Intern", "Business Analyst Intern", "Operations Intern",
  "Supply Chain Intern", "Logistics Intern", "Procurement Intern", "Program Management Intern",
  "Project Management Intern",
  // Marketing / sales / comms
  "Marketing Intern", "Brand Marketing Intern", "Digital Marketing Intern", "Social Media Intern",
  "Content Marketing Intern", "SEO Analyst Intern", "Public Relations Intern", "Communications Intern",
  "Advertising Intern", "Sales Intern", "Business Development Intern", "Account Management Intern",
  "Customer Success Intern", "Merchandising Intern", "Buyer Intern", "Event Planning Intern",
  // People
  "Human Resources Intern", "Recruiting Intern", "Talent Acquisition Intern",
  // Healthcare / bio
  "Nursing Extern", "Clinical Research Intern", "Medical Assistant Intern", "Public Health Intern",
  "Biomedical Engineering Intern", "Lab Research Assistant", "Pharmacy Intern",
  // Engineering (non-software)
  "Mechanical Engineering Intern", "Civil Engineering Intern", "Electrical Engineering Intern",
  "Chemical Engineering Intern", "Industrial Engineering Intern", "Manufacturing Engineering Intern",
  "Aerospace Engineering Intern", "Environmental Engineering Intern",
  // Design / media
  "Graphic Design Intern", "UX/UI Design Intern", "Product Design Intern", "Architecture Intern",
  "Interior Design Intern", "Journalism Intern", "Editorial Intern", "Video Production Intern",
  "Game Design Intern",
  // Other
  "Real Estate Analyst Intern", "Property Management Intern", "Environmental Science Intern",
  "Sustainability Intern", "Policy Research Intern", "Legal Intern", "Paralegal Intern",
  "Economics Research Intern", "Hospitality Intern", "Research Intern", "Teaching Assistant Intern",
]

// Common college majors (CIP-aligned display names). Curated, not exhaustive.
const RAW_MAJORS = [
  "Computer Science", "Artificial Intelligence", "Machine Learning", "Data Science", "Data Analytics",
  "Statistics", "Applied Mathematics", "Mathematics", "Computer Engineering", "Electrical Engineering",
  "Software Engineering", "Information Systems", "Information Science and Technology", "Cybersecurity",
  "Finance", "Economics", "Accounting", "Business Administration", "Management Information Systems",
  "Mechanical Engineering", "Industrial Engineering", "Aerospace Engineering", "Chemical Engineering",
  "Biomedical Engineering", "Civil Engineering", "Physics", "Chemistry", "Biology", "Neuroscience",
  "Cognitive Science", "Operations Research", "Financial Engineering", "Actuarial Science", "Marketing",
  "Supply Chain Management", "Management", "Psychology", "Communications", "Political Science",
  "International Relations", "Public Policy", "Mathematical Economics", "Quantitative Finance",
]

// Common US universities (state flagships + big publics + top privates + Max's school).
// Curated for autocomplete consistency; a school not listed can still be typed.
const RAW_US_UNIVERSITIES = [
  "Pennsylvania State University", "University of Pittsburgh", "Carnegie Mellon University",
  "Temple University", "Drexel University", "Villanova University", "Lehigh University",
  "Massachusetts Institute of Technology", "Harvard University", "Stanford University",
  "University of California, Berkeley", "University of California, Los Angeles",
  "University of California, San Diego", "University of Southern California", "California Institute of Technology",
  "Princeton University", "Yale University", "Columbia University", "Cornell University",
  "University of Pennsylvania", "Brown University", "Dartmouth College", "New York University",
  "University of Michigan", "University of Illinois Urbana-Champaign", "University of Wisconsin-Madison",
  "University of Texas at Austin", "Georgia Institute of Technology", "University of Washington",
  "University of California, Irvine", "University of California, Davis", "University of California, Santa Barbara",
  "Purdue University", "Ohio State University", "University of Maryland", "University of Virginia",
  "University of North Carolina at Chapel Hill", "Duke University", "University of Notre Dame",
  "Boston University", "Northeastern University", "University of Florida", "University of Georgia",
  "Rutgers University", "University of Minnesota", "University of Colorado Boulder", "Arizona State University",
  "University of Arizona", "Texas A&M University", "Rice University", "Vanderbilt University",
  "Washington University in St. Louis", "University of Chicago", "Northwestern University",
  "Johns Hopkins University", "University of Rochester", "Rensselaer Polytechnic Institute",
  "Stevens Institute of Technology", "Rochester Institute of Technology", "Worcester Polytechnic Institute",
  "Virginia Tech", "University of California, Santa Cruz", "Michigan State University",
  "Indiana University Bloomington", "University of Massachusetts Amherst", "Stony Brook University",
  "University of Connecticut", "University of Miami", "Emory University", "Tufts University",
  "Case Western Reserve University", "University of Utah", "University of Iowa", "Iowa State University",
  "University of Tennessee", "North Carolina State University", "University of California, Riverside",
  "George Washington University", "Georgetown University", "Boston College", "Brigham Young University",
  "University of Notre Dame", "University of Kansas", "University of Oregon", "University of Kentucky",
]

/** Case-insensitive contains, capped — for building datalist option subsets if needed. */
export function filterList(list: string[], q: string, limit = 20): string[] {
  const s = q.trim().toLowerCase()
  if (!s) return list.slice(0, limit)
  return list.filter((x) => x.toLowerCase().includes(s)).slice(0, limit)
}

// ── alphabetized exports (dropdown display order) ──
export const INTEREST_AREAS = ascAlpha(RAW_INTEREST_AREAS)
export const INDUSTRIES = ascAlpha(RAW_INDUSTRIES)
export const ROLE_TITLES = ascAlpha(RAW_ROLE_TITLES)
export const MAJORS = ascAlpha(RAW_MAJORS)
export const US_UNIVERSITIES = ascAlpha(RAW_US_UNIVERSITIES)
export const LOCATIONS = pinnedAscAlpha(["Anywhere", "Remote"], RAW_LOCATIONS)

// ── Location canonicalization (dedup) ─────────────────────────────────────────
// desired_locations accumulates messy free-text + imports: "NYC" + "New York", "SF" +
// "San Francisco, CA", "Boston, MA". This maps each entry to ONE canonical name and dedups,
// so a student's saved locations and the /feed Locations bubbles read clean. Conservative:
// it only rewrites entries it RECOGNIZES (a known city, a common alias, or "City, ST" whose
// city part is a known city); anything unrecognized is kept exactly as typed.
const CANON_BY_LOWER = new Map(LOCATIONS.map((l) => [l.toLowerCase(), l] as const))
// Common abbreviations/nicknames -> the canonical RAW_LOCATIONS spelling.
const CITY_ALIASES: Record<string, string> = {
  nyc: "New York", ny: "New York", manhattan: "New York",
  sf: "San Francisco", sfo: "San Francisco", "san fran": "San Francisco",
  la: "Los Angeles",
  dc: "Washington DC", "washington d c": "Washington DC", "washington, dc": "Washington DC",
  philly: "Philadelphia",
}
function canonicalizeLocation(raw: string): string | null {
  const clean = String(raw || "").replace(/\s+/g, " ").trim()
  if (!clean) return null
  const lower = clean.toLowerCase()
  if (lower === "remote") return "Remote"
  if (lower === "anywhere") return "Anywhere"
  if (CITY_ALIASES[lower]) return CITY_ALIASES[lower]
  if (CANON_BY_LOWER.has(lower)) return CANON_BY_LOWER.get(lower)!
  // "City, Region" ("San Francisco, CA", "Toronto, Canada"): map/keep the city part, dropping the
  // trailing state/country so "San Francisco, CA" and "SF" collapse, and "Toronto, Canada" and
  // "Toronto" collapse. Only strip when the suffix looks like a plain state/country (short, letters
  // only) so a genuinely comma-bearing place name is left intact.
  const comma = clean.lastIndexOf(",")
  if (comma > 0) {
    const base = clean.slice(0, comma).trim()
    const region = clean.slice(comma + 1).trim()
    const baseLower = base.toLowerCase()
    if (CITY_ALIASES[baseLower]) return CITY_ALIASES[baseLower]
    if (CANON_BY_LOWER.has(baseLower)) return CANON_BY_LOWER.get(baseLower)!
    if (base.length >= 2 && /^[a-z .]{2,20}$/i.test(region)) return base
  }
  return clean
}
/** Canonicalize + de-duplicate a list of location strings (order preserved, first canonical wins). */
export function canonicalizeLocations(list: readonly string[] | null | undefined): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of list ?? []) {
    const c = canonicalizeLocation(raw)
    if (!c) continue
    const k = c.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(c)
  }
  return out
}

// ── Skills vocabulary (suggestion lists, NOT a closed taxonomy) ────────────────
// The career_skills table stores skill/category/proficiency as free text. These
// standardise the Skills form (a datalist of common skills, a category picker, a
// proficiency select) and let résumé-seeded skills carry a sensible category —
// while the user can still type anything. Ordered for display.
export const PROFICIENCY_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"]

export const SKILL_CATEGORIES = [
  "Programming", "Web & Mobile", "Data & ML", "Cloud & DevOps", "Databases",
  "Design", "Product & PM", "Business & Finance", "Tools & Platforms", "Other",
]

// Common skills for the Skills-form datalist (free text still allowed).
export const COMMON_SKILLS = ascAlpha([
  "Python", "Java", "JavaScript", "TypeScript", "C", "C++", "C#", "Go", "Rust", "Ruby", "Swift", "Kotlin", "Scala", "R", "MATLAB", "SQL",
  "React", "Next.js", "Node.js", "Vue", "Angular", "HTML", "CSS", "Tailwind CSS", "React Native", "Flutter",
  "FastAPI", "Flask", "Django", "Spring", "Express", "GraphQL", "REST APIs",
  "PostgreSQL", "MySQL", "MongoDB", "Redis", "Snowflake", "BigQuery",
  "PyTorch", "TensorFlow", "scikit-learn", "Pandas", "NumPy", "Spark", "Hadoop",
  "AWS", "Google Cloud", "Azure", "Docker", "Kubernetes", "Terraform", "CI/CD",
  "Git", "GitHub", "GitLab", "Linux", "Bash", "Jira", "Figma", "Excel", "Tableau", "Power BI",
])

// Light heuristic: map a skill name to a standard category (for résumé seeding + as a
// suggestion). Returns null when unknown — category is optional, never invented loudly.
const SKILL_CATEGORY_HINTS: { re: RegExp; category: string }[] = [
  // Web/mobile + the common web frameworks (front- AND back-end) students list.
  { re: /^(react|next\.?js|vue|nuxt|angular|svelte|remix|astro|html|css|sass|tailwind.*|bootstrap|jquery|react native|flutter|ionic|expo|fastapi|flask|django|spring( boot)?|express(\.?js)?|nest(\.?js)?|node(\.?js)?|deno|rails|ruby on rails|laravel|\.net|asp\.net|graphql|rest ?apis?|websockets?)$/i, category: "Web & Mobile" },
  { re: /^(pytorch|tensorflow|scikit-?learn|sklearn|pandas|numpy|scipy|matplotlib|seaborn|spark|pyspark|hadoop|keras|opencv|hugging ?face|transformers|langchain|nlp|computer vision|machine learning|deep learning|data science|jupyter|xgboost|lightgbm|llms?)$/i, category: "Data & ML" },
  { re: /^(aws|amazon web services|google cloud|gcp|azure|docker|kubernetes|k8s|terraform|ci\/cd|jenkins|ansible|github actions|circleci|helm|serverless|lambda|cloudformation)$/i, category: "Cloud & DevOps" },
  { re: /^(postgres(ql)?|mysql|mariadb|mongodb|redis|snowflake|bigquery|sqlite|oracle|dynamodb|cassandra|elasticsearch|firebase|supabase|dbt|databricks)$/i, category: "Databases" },
  { re: /^(figma|sketch|adobe xd|photoshop|illustrator|indesign|framer|canva|prototyping|wireframing|ui design|ux design|ui\/ux)$/i, category: "Design" },
  { re: /^(jira|confluence|asana|linear|trello|agile|scrum|kanban|user research|a\/b testing|product management|roadmapping|okrs?)$/i, category: "Product & PM" },
  { re: /^(excel|google sheets|tableau|power ?bi|looker|financial modeling|accounting|valuation|bloomberg|quickbooks|sap|salesforce|hubspot|dcf)$/i, category: "Business & Finance" },
  { re: /^(git|github|gitlab|bitbucket|linux|unix|bash|shell|powershell|notion|slack|vs ?code|jetbrains|intellij|postman|jira)$/i, category: "Tools & Platforms" },
  { re: /^(python|java|javascript|typescript|c\+\+|c#|c|go|golang|rust|ruby|swift|kotlin|scala|r|matlab|sql|php|perl|haskell|objective-c|dart|elixir|clojure|solidity|assembly|vhdl|verilog)$/i, category: "Programming" },
]
export function skillCategory(skill: string): string | null {
  const s = (skill || "").trim()
  for (const { re, category } of SKILL_CATEGORY_HINTS) if (re.test(s)) return category
  return null
}
